import { useTranslation } from '@/i18n';

/** 1 is the worst, 5 the best. The same scale TouchTab.Feedback stores and alerts on. */
export const RATINGS = [1, 2, 3, 4, 5] as const;
export type Rating = (typeof RATINGS)[number];

interface RatingScaleProps {
    /** Groups the five radios. Must be unique on the page — one question, one name. */
    name: string;
    /** The question, as the restaurant authored it. Becomes the group's legend. */
    question: string;
    value: Rating | null;
    onChange: (rating: Rating | null) => void;
    disabled?: boolean;
}

/**
 * One question, five faces.
 *
 * The same scale as the handheld's `FeedbackComponent` — five faces from very dissatisfied to very satisfied,
 * each over its own number of stars — so a guest rating on their phone and a server rating on a handheld are
 * answering the same question the same way, and the numbers land in one report meaning one thing.
 *
 * Drawn rather than shipped as images. The handheld uses tinted PNG drawables, which cannot take a
 * restaurant's brand ink or survive dark mode; these are `currentColor` and do both, and cost no requests.
 *
 * Native radios under the faces rather than buttons with ARIA. A radio group gives arrow-key navigation, the
 * "2 of 5" position, and the group's legend read before each option, for free and correctly. The input is
 * visually hidden but focusable, and the visible face is the label — so a tap anywhere on it selects, and the
 * focus ring lands on something the guest can see.
 */
export function RatingScale({ name, question, value, onChange, disabled }: RatingScaleProps) {
    const { t } = useTranslation();

    return (
        <fieldset disabled={disabled} className="min-w-0">
            <legend className="mb-2 w-full text-[0.9375rem] font-medium text-pretty">{question}</legend>

            <div className="grid grid-cols-5 gap-1.5">
                {RATINGS.map((rating) => {
                    const selected = value === rating;

                    return (
                        <label
                            key={rating}
                            className={[
                                'flex cursor-pointer flex-col items-center gap-1 rounded-xl border py-2',
                                'transition-colors',
                                // 1.4.1: the border and weight change carry the selection too, so it does not
                                // depend on telling the brand colour from the muted one.
                                selected
                                    ? 'border-primary bg-primary/10 text-primary'
                                    : 'border-transparent text-muted-foreground',
                                // The ring follows the hidden input's focus onto the label the guest can see.
                                'has-[:focus-visible]:outline has-[:focus-visible]:outline-3',
                                'has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-foreground',
                            ].join(' ')}
                        >
                            <input
                                type="radio"
                                name={name}
                                value={rating}
                                checked={selected}
                                aria-label={t(`feedback.rating.${rating}`)}
                                onChange={() => onChange(rating)}
                                /*
                                 * Tapping the chosen face again clears it, as the handheld does. A radio
                                 * cannot be unchecked by clicking, so the click is caught before the browser
                                 * acts on it. Only a pointer can do this — a keyboard cannot uncheck a radio
                                 * either — but a guest who mistaps has no other way back to "not answered",
                                 * and the button below says "Skip" until something is rated.
                                 */
                                onClick={(event) => {
                                    if (!selected) return;
                                    event.preventDefault();
                                    onChange(null);
                                }}
                                className="sr-only"
                            />

                            <Face rating={rating} />
                            <Stars count={rating} filled={selected} />
                        </label>
                    );
                })}
            </div>
        </fieldset>
    );
}

/*
 * Five faces, told apart by the mouth alone. Eyes and outline are identical across all five so the difference
 * a guest reads is the one that means something, and the stars beneath say the same thing again in a second
 * form for anyone who finds the faces ambiguous.
 */
const MOUTHS: Record<Rating, string> = {
    1: 'M14 34 Q24 20 34 34',
    2: 'M16 31.5 Q24 26.5 32 31.5',
    3: 'M15 30 H33',
    4: 'M16 28.5 Q24 33.5 32 28.5',
    5: 'M14 26 Q24 40 34 26',
};

function Face({ rating }: { rating: Rating }) {
    return (
        <svg viewBox="0 0 48 48" className="size-9" fill="none" aria-hidden="true">
            <circle cx="24" cy="24" r="21" stroke="currentColor" strokeWidth="2.5" />
            <circle cx="17.5" cy="19" r="2.4" fill="currentColor" />
            <circle cx="30.5" cy="19" r="2.4" fill="currentColor" />
            <path
                d={MOUTHS[rating]}
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
            />
        </svg>
    );
}

/*
 * One star per point, as on the handheld. Decorative: the radio's own label already says "3 of 5", and
 * announcing five more things per option would bury it.
 */
function Stars({ count, filled }: { count: number; filled: boolean }) {
    return (
        <span className="flex gap-px" aria-hidden="true">
            {Array.from({ length: count }, (_, index) => (
                <svg key={index} viewBox="0 0 24 24" className="size-2" fill={filled ? 'currentColor' : 'none'}>
                    <path
                        d="M12 2.5 14.9 9l7.1.6-5.4 4.7 1.6 6.9L12 17.6 5.8 21.2l1.6-6.9L2 9.6 9.1 9Z"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinejoin="round"
                    />
                </svg>
            ))}
        </span>
    );
}
